import { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Tooltip,
    Alert,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    ContentCopy as DuplicateIcon,
    AccountTree as FlowIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';

export interface Flow {
    id: number;
    name: string;
    active: boolean;
    description?: string;
    createdAt: string;
    updatedAt: string;
}

export const FlowBuilder = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParam, setSearchParam] = useState('');

    // Buscar fluxos
    const { data: flows = [], isLoading } = useQuery({
        queryKey: ['flows', searchParam],
        queryFn: async () => {
            const response = await api.get('/atendimento/fluxos') as any;
            return response.flows as Flow[];
        },
    });

    // Deletar fluxo
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`/atendimento/fluxos/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['flows'] });
            toast.success('Fluxo deletado com sucesso!');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Erro ao deletar fluxo');
        },
    });

    // Duplicar fluxo
    const duplicateMutation = useMutation({
        mutationFn: async (id: number) => {
            await api.post('/atendimento/fluxos/duplicate', { flowId: id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['flows'] });
            toast.success('Fluxo duplicado com sucesso!');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Erro ao duplicar fluxo');
        },
    });

    const handleCreate = async () => {
        try {
            const name = window.prompt('Digite o nome do novo fluxo:');
            if (name) {
                const response = await api.post('/atendimento/fluxos', { name }) as any;
                navigate(`/atendimento/fluxo-bot/${response.flow.id}`);
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erro ao criar fluxo');
        }
    };

    const handleEdit = (id: number) => {
        navigate(`/atendimento/fluxo-bot/${id}`);
    };

    const handleDelete = (flow: Flow) => {
        if (window.confirm(`Tem certeza que deseja deletar o fluxo "${flow.name}"?`)) {
            deleteMutation.mutate(flow.id);
        }
    };

    const handleDuplicate = (flow: Flow) => {
        duplicateMutation.mutate(flow.id);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    const filteredFlows = flows.filter(f =>
        f.name.toLowerCase().includes(searchParam.toLowerCase())
    );

    return (
        <Box sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight={600}>
                        Fluxos de Conversa
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Crie e gerencie os fluxos de mensagens para o chatbot.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Novo Fluxo
                </Button>
            </Box>

            <Paper sx={{ p: 1.5, mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Pesquisar fluxos..."
                    value={searchParam}
                    onChange={(e) => setSearchParam(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {filteredFlows.length === 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                    Nenhum fluxo encontrado. Clique em "Novo Fluxo" para começar.
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'background.default' }}>
                                <TableCell sx={{ py: 1, fontWeight: 600 }}>Nome</TableCell>
                                <TableCell sx={{ py: 1, fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ py: 1, fontWeight: 600 }}>Criado em</TableCell>
                                <TableCell align="right" sx={{ py: 1, fontWeight: 600 }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredFlows.map((flow) => (
                                <TableRow key={flow.id} hover>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FlowIcon color="primary" sx={{ fontSize: 20 }} />
                                            <Typography variant="body2" fontWeight={500}>{flow.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        {flow.active ? (
                                            <Typography variant="body2" color="success.main" fontWeight={500}>Ativo</Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">Inativo</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography variant="body2">
                                            {new Date(flow.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 0.5 }}>
                                        <Tooltip title="Editar Fluxo">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(flow.id)}
                                                color="primary"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Duplicar">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDuplicate(flow)}
                                                color="info"
                                            >
                                                <DuplicateIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Deletar">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(flow)}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
